import { BasePlugin } from '@uppy/core';
import { filterNonFailedFiles, filterFilesToEmitUploadStarted } from '@uppy/utils/lib/fileFilters';

import CloudinaryApiClient from './CloudinaryApiClient';

/**
 * Uppy plugin that uploads selected files to Cloudinary.
 */
export default class CloudinaryPlugin extends BasePlugin {
  /**
   * @param {Uppy}      uppy
   * @param {Object}    options
   * @param {String}    [options.id]
   * @param {String}    [options.cloudName]
   * @param {String}    [options.uploadPreset]
   * @param {String}    [options.apiKey]
   * @param {String}    [options.folder]
   * @param {String[]}  [options.tags]
   * @param {Function}  [options.createUploadRequest]
   */
  constructor(uppy, options) {
    super(uppy, options);

    const { id, apiKey, cloudName, folder, tags, uploadPreset, createUploadRequest } = options;

    this.id = id || 'CloudinaryPlugin';
    this.type = 'uploader';

    this.apiClient = new CloudinaryApiClient({
      apiKey: apiKey,
      cloudName: cloudName,
      folder: folder,
      tags: tags,
      uploadPreset: uploadPreset,
      createUploadRequest: createUploadRequest,
    });
  }

  /**
   * @param {UppyFile} file
   * @returns {Promise<Object>}
   */
  uploadFile = async (file) => {
    try {
      const response = await this.apiClient.upload(file.data, {
        onUploadProgress: (event) => {
          if (!event.lengthComputable) {
            return;
          }

          const latestFile = this.uppy.getFile(file.id);

          // Inform Uppy instance of the current progress
          this.uppy.emit('upload-progress', latestFile, {
            uploadStarted: latestFile.progress.uploadStarted ?? 0,
            bytesUploaded: (event.loaded / event.total) * latestFile.size,
            bytesTotal: latestFile.size,
          });
        },
      });

      this.uppy.emit('upload-success', this.uppy.getFile(file.id), response);

      return response;
    } catch (error) {
      this.uppy.emit('upload-error', file, error);
      throw error;
    }
  };

  /**
   * @param {String[]} fileIDs
   * @returns {Promise}
   */
  uploadFiles = async (fileIDs) => {
    if (fileIDs.length === 0) return undefined;

    const files = this.uppy.getFilesByIds(fileIDs);
    const filesFiltered = filterNonFailedFiles(files);
    const filesToEmit = filterFilesToEmitUploadStarted(filesFiltered);

    this.uppy.emit('upload-start', filesToEmit);

    const promises = filesFiltered.map((file) => {
      return this.uploadFile(file);
    });

    return Promise.allSettled(promises);
  };

  install() {
    this.uppy.addUploader(this.uploadFiles);
  }

  uninstall() {
    this.uppy.removeUploader(this.uploadFiles);
  }
}
